import type { Trip, TripParticipant, TripShare } from '@my-fullstack-app/itinerary-types';
import type { UserInfo } from '../contexts/AuthContext';
import { tripCostBearers, validLineItems } from './cost';

/** UI display label for a participant. Kept as a tiny indirection
 *  (rather than reaching into `.displayName` at every call site)
 *  so future label-shape changes only edit one place — same role
 *  it played before the ADR-0057 inline-edit amendment removed
 *  the `friendlyName` fallback. */
export function getParticipantDisplayName(
  participant: Pick<TripParticipant, 'displayName'>,
): string {
  return participant.displayName;
}

/**
 * Render-friendly label for a TripShare. Used by both ShareTripSheet
 * and the PeopleTab quick-add chips so the two surfaces never disagree
 * on what a person is called. Falls back through name → full email →
 * short userId fragment. Keeps the FULL email (not the local-part)
 * because trimming the domain makes bob@gmail.com and bob@aol.com
 * render identically in the share list.
 */
export function shareDisplayName(share: TripShare): string {
  const name = share.displayName?.trim();
  if (name) return name;
  const email = share.email?.trim();
  if (email) return email;
  return `User ${share.userId.slice(0, 6)}`;
}

/**
 * The non-name identity hint for a TripShare — i.e. the email if
 * present, otherwise a short userId fragment. Used as the secondary
 * line under `shareDisplayName` so a user-controlled displayName
 * can't visually impersonate someone else: the real email (set by
 * the inviter) stays visible. Returns null when the primary label
 * IS the email/userId-fragment (so the secondary row collapses).
 */
export function shareSecondaryIdentity(share: TripShare): string | null {
  const name = share.displayName?.trim();
  if (!name) return null;
  const email = share.email?.trim();
  if (email) return email;
  return null;
}

/**
 * Two-letter avatar initials for a TripShare. Skips the literal
 * `User abc123` fallback string (which would collapse every nameless
 * share to "US") and skips the `@` character in email locals (which
 * would render as "A@" for `a@example.com`). All ShareTripSheet /
 * LinkParticipantSheet / PeopleTab avatar surfaces should share this
 * — otherwise the same person renders different initials in adjacent
 * sheets.
 */
export function shareInitials(share: TripShare): string {
  const name = share.displayName?.trim();
  if (name) return name.slice(0, 2).toUpperCase();
  const email = share.email?.trim();
  if (email) {
    const local = email.split('@')[0] ?? '';
    if (local) return local.slice(0, 2).toUpperCase();
  }
  return share.userId.slice(0, 2).toUpperCase();
}

/**
 * Compact name to seed a newly-added `TripParticipant.displayName`
 * from a `TripShare` (quick-add chip flow). Uses the email's local-
 * part rather than the full address so chips, day-timeline labels,
 * and cost-attribution rows stay short. The share row in
 * ShareTripSheet still renders the full email via `shareDisplayName`
 * — the two surfaces have different size budgets.
 */
export function participantSeedDisplayName(share: TripShare): string {
  const name = share.displayName?.trim();
  if (name) return name;
  const email = share.email?.trim();
  if (email) {
    const local = email.split('@')[0] ?? '';
    if (local) return local;
    return email;
  }
  return `User ${share.userId.slice(0, 6)}`;
}

/**
 * Build the owner-participant seed for `createTrip` from an
 * authenticated user. Mirrors the quick-add chip flow: prefer the
 * user's profile name, fall back to the email's local-part so the
 * timeline label stays short. Used by every new-trip surface
 * (manual / AI) so the trip creator lands on `participants[]`
 * automatically and cost defaults work from the first entry.
 */
export function ownerFromUser(
  user: Pick<UserInfo, 'id' | 'displayName' | 'email' | 'avatarUrl'>,
): { userId: string; displayName: string; avatarUrl?: string | null | undefined } | undefined {
  // Empty `user.id` is reachable when the JWT lacks a `sub` claim
  // (AuthContext synthesises id via `String(payload.sub ?? '')`). The
  // wire schema enforces `userId: z.string().min(1).optional()`, so
  // shipping `userId: ''` would 400 every `updateTrip` flush. Bail
  // out so the trip is created without an auto-seeded participant —
  // the user can add themselves manually once they have a valid id.
  if (!user.id) return undefined;
  const name = user.displayName?.trim();
  let displayName: string;
  if (name) {
    displayName = name;
  } else {
    const local = user.email.split('@')[0]?.trim() ?? '';
    displayName = local || user.email || 'Me';
  }
  return {
    userId: user.id,
    displayName,
    ...(user.avatarUrl ? { avatarUrl: user.avatarUrl } : {}),
  };
}

/**
 * Filter trip shares down to those not yet linked to any participant.
 * Used by both the People-tab quick-add chips and the per-participant
 * link picker. When `excludeParticipantId` is supplied, that
 * participant's own `userId` is treated as not-blocking — so the link
 * picker can still show the row that's currently linked (e.g. so the
 * user can see who they're already linked to).
 *
 * Per ADR-0057 ("Linking semantics"): only TripShares whose userId
 * isn't already pointed at by any other participant are eligible.
 */
export function filterUnlinkedShares(
  participants: readonly TripParticipant[],
  shares: readonly TripShare[],
  excludeParticipantId?: string,
): TripShare[] {
  const blocked = new Set<string>();
  for (const p of participants) {
    if (!p.userId) continue;
    if (excludeParticipantId !== undefined && p.id === excludeParticipantId) continue;
    blocked.add(p.userId);
  }
  return shares.filter((s) => !blocked.has(s.userId));
}

/**
 * Pick the natural display currency for a trip's settlement view —
 * the most-frequent ISO 4217 code across every line item, with
 * alphabetical ties for stability. Falls back to `preferred` (the
 * user's preference), then to `'USD'`.
 *
 * Codes are normalised to upper-case.
 */
export function inferDisplayCurrency(trip: Trip, preferred: string | undefined): string {
  const counts = new Map<string, number>();
  for (const bearer of tripCostBearers(trip)) {
    for (const { currency } of validLineItems(bearer)) {
      counts.set(currency, (counts.get(currency) ?? 0) + 1);
    }
  }
  if (counts.size === 0) return (preferred?.trim() || 'USD').toUpperCase();
  let best: { cur: string; n: number } | undefined;
  for (const [cur, n] of counts) {
    if (!best || n > best.n || (n === best.n && cur < best.cur)) best = { cur, n };
  }
  return best!.cur;
}

/** All distinct ISO codes used across a trip's line items, sorted
 *  alphabetically. Used to populate the settlement currency picker. */
export function tripCurrencies(trip: Trip): string[] {
  const set = new Set<string>();
  for (const bearer of tripCostBearers(trip)) {
    for (const { currency } of validLineItems(bearer)) {
      set.add(currency);
    }
  }
  return Array.from(set).sort();
}
